import React from "react"
import { TitleTag } from "./titleTag"
import { Description } from "./description"
import { Keywords } from "./keywords"
import { OpenGraph } from "./openGraph"
import { OGVideo } from "./ogVideo"
import { OGProfile } from "./ogProfile"
import { OGTwitter } from "./ogTwitter"
import { Canonical } from "./canonical"

// Defaults - the default values we use in the <Head> of our pages
export enum Default {
  SiteName = "Bonsai",
  Tagline = "Create a moment with an artist you love",
  Description = "Ask a question. Get an audiogram. Support an artist you love.",
  Keywords = "creators, musicians, fans, artists, Bonsai, AMA, audiogram, Q&A, collectible, NFT, Patrick Sullivan, Jake Rosenfeld",
  FeaturedImage = "https://joinbonsai.co/images/bonsai-card-300.png",
  FeaturedImageAlt = "Bonsai — Ask a question. Get an audiogram. Support an artist you love.",
  FeaturedImageWidth = "300",
  FeaturedImageHeight = "300",
  SiteURL = "https://joinbonsai.co",
  IconPath = "/images/icons/",
  Type = "website",
}

interface ProfileProps {
  profileName: string
}

interface FeaturedVideoProps {
  date: string
  url: string
}
interface FeaturedImgProps {
  imgurl: string
  imgalt: string
  imgwidth?: string
  imgheight?: string
}

export interface MetaProps {
  title?: string
  url?: string
  description?: string
  keywords?: string
  featuredImage?: FeaturedImgProps
  rel?: string
  name?: string
  size?: string
  type?: string
  content?: string
  featuredVideo?: FeaturedVideoProps
  profile?: ProfileProps
  twitterCard?: string
}

// The required names and keys for the default <meta> tags
// Also the unique keys that NextJS uses to remove any duplicates tags
export enum MetaKey {
  Title = "title",
  Description = "description",
  Keywords = "keywords",
  Canonical = "canonical",
}

// The required types for the default <meta> tags
// Also the unique keys that NextJS uses to remove any duplicates tags
export enum MetaType {
  Website = "website",
  Article = "article",
  Image = "image/jpeg",
  Video = "video",
  VideoOther = "video.other",
  Profile = "profile",
  ProfileName = "profile:first_name",
  FacebookAppId = "1435401960281187",
  TwitterSite = "@joinbonsai",
  TwitterCreator = "@joinbonsai",
  TwitterCardPlayer = "player",
  TwitterCardSummary = "summary",
  TwitterCardSummaryLarge = "summary_large_image",
}

// The values for the 'property` attribute in the OpenGrap <meta> tags
// Also the unique keys that NextJS uses to remove any duplicates tags
export enum OG {
  Title = "og:title",
  SiteName = "og:site_name",
  Description = "og:description",
  Url = "og:url",
  Image = "og:image",
  ImageAlt = "og:image:alt",
  ImageType = "og:image:type",
  ImageWidth = "og:image:width",
  ImageHeight = "og:image:height",
  Type = "og:type",
  ProfileArtist = "og:profile",
  ProfileFan = "og:profile",
  ProfileName = "profile:first_name",
  Card = "twitter:card",
  VideoURL = "og:video",
  VideoSecureURL = "og:video:secure_url",
  VideoType = "og:video:type",
  VideoWidth = "og:video:width",
  VideoHeight = "og:video:height",
  VideoDuration = "og:video:duration",
  VideoReleaseDate = "og:video:release_date",
  VideoActor = "og:video:actor",
  TwitterSite = "twitter:site",
  FacebookAppId = "fb:app_id",
  TwitterCreator = "twitter:creator",
  TwitterTitle = "twitter:title",
  TwitterDescription = "twitter:description",
  TwitterCard = "twitter:card",
  TwitterImage = "twitter:image",
  TwitterImageAlt = "twitter:image:alt",
  TwitterPlayer = "twitter:player",
  TwitterPlayerHeight = "twitter:player:height",
  TwitterPlayerWidth = "twitter:player:width",
}

const MetaTags = (props: MetaProps) => {
  const { type, featuredVideo, profile } = props
  return (
    <>
      <TitleTag {...props} />
      <Description {...props} />
      <Keywords {...props} />
      <OpenGraph {...props} />
      <OGTwitter {...props} />
      <Canonical {...props} />
      {type == MetaType.VideoOther && featuredVideo && <OGVideo {...featuredVideo} />}
      {type == MetaType.Profile && profile && <OGProfile {...profile} />}
    </>
  )
}

export default MetaTags
